import { useMemo } from 'react';
import { AlertTriangle, Flag } from 'lucide-react';
import { Link } from 'react-router-dom';

import PickupHandoff from '../../pages/delivery/PickupHandoff';
import OperationalMap from '../../../features/delivery/maps/OperationalMap';

// Everything here is read from the trip the server returned. A time that was
// not recorded is left out; nothing is inferred.

const readable = (value) => String(value || '').replaceAll('_', ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());
const STOP_STATUS = Object.freeze({
  QUEUED: 'Waiting', ACTIVE: 'Rider heading there', ARRIVED: 'Rider at the customer', DELIVERED: 'Delivered',
  ATTEMPT_FAILED: 'Attempt failed', RESCHEDULED: 'Rescheduled', CANCELLED: 'Removed from trip',
});
const COD_STATUS = Object.freeze({
  DUE: 'to collect', COLLECTED: 'collected by the rider', REMITTED: 'handed in', RECONCILED: 'reconciled',
  VOIDED: 'not collected', MISSING: 'no cash record',
});
const PROOF_STATUS = Object.freeze({ FINALIZED: 'Photo saved', APPROVED: 'Photo approved', REJECTED: 'Photo rejected' });

export function tripStatusLabel(run = {}) {
  if (run.status === 'PLANNED') return run.pickupStartedAt ? 'Collecting parcels' : 'Preparing pickup';
  return { READY: 'Ready to deliver', ACTIVE: 'On the road', INTERRUPTED: 'Interrupted', COMPLETED: 'Completed', CANCELLED: 'Cancelled' }[run.status]
    || readable(run.status) || 'Status unavailable';
}
const dateTime = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : new Intl.DateTimeFormat('en-PH', { dateStyle: 'medium', timeStyle: 'short' }).format(parsed);
};
const peso = (cents) => new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(Number(cents || 0) / 100);
const distance = (meters) => meters == null || !Number.isFinite(Number(meters)) ? null
  : Number(meters) >= 1000 ? `${(Number(meters) / 1000).toFixed(1)} km` : `${Number(meters)} m`;
const duration = (seconds) => seconds == null || !Number.isFinite(Number(seconds)) ? null
  : Number(seconds) > 0 && Number(seconds) < 60 ? 'under 1 min' : `${Math.max(0, Math.round(Number(seconds) / 60))} min`;

const stopFailed = (stop) => stop.status === 'ATTEMPT_FAILED' || stop.attempt?.status === 'FAILED';

export function tripSummary(run = {}) {
  const stops = (run.stops || []).filter((stop) => stop.status !== 'CANCELLED');
  const delivered = stops.filter((stop) => stop.status === 'DELIVERED').length;
  const failed = stops.filter((stop) => stop.status !== 'DELIVERED' && stopFailed(stop)).length;
  const cash = stops.filter((stop) => stop.cod);
  return {
    total: stops.length, delivered, failed, open: stops.length - delivered - failed,
    removed: (run.stops || []).length - stops.length,
    cashParcels: cash.length,
    cashExpectedCents: cash.reduce((sum, stop) => sum + Number(stop.cod.expectedAmountCents || 0), 0),
    cashCollectedCents: cash.reduce((sum, stop) => sum + Number(stop.cod.collectedAmountCents || 0), 0),
    flaggedProofs: stops.filter((stop) => stop.attempt?.proof?.reviewRequired).length,
  };
}

export function tripActivity(run = {}) {
  const events = [
    [run.plannedAt, 'Trip planned'],
    [run.pickupStartedAt, 'Rider set off for the store'],
    [run.pickupArrival?.capturedAt, 'Rider arrived at the store'],
    [run.handoff?.verifiedAt, 'Store checked the parcels at handoff'],
    [run.handoff?.confirmedAt, 'Rider confirmed the pickup'],
    [run.startedAt, 'Trip started'],
    ...(run.stops || []).flatMap((stop) => {
      const name = stop.orderReference || `stop ${stop.sequence}`;
      return [
        [stop.arrivedAt, `Arrived at ${name}`],
        [stop.completedAt, `${STOP_STATUS[stop.status] || readable(stop.status)}: ${name}`],
      ];
    }),
    [run.completedAt, 'Trip completed'],
    [run.cancelledAt, 'Trip cancelled'],
  ];
  return events.filter(([at]) => dateTime(at))
    .map(([at, label], index) => ({ at, label, index }))
    .sort((a, b) => new Date(a.at) - new Date(b.at) || a.index - b.index);
}

const panel = 'rounded-2xl border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-950';
const focusRing = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus';
const textLink = `font-semibold underline underline-offset-4 ${focusRing}`;

function Fact({ term, children }) {
  return <div className="min-w-0"><dt className="text-xs text-neutral-600 dark:text-neutral-400">{term}</dt><dd className="mt-0.5 break-words text-sm font-medium">{children || 'Not recorded'}</dd></div>;
}

export default function TripRecord({ run, businessId, can, onUpdated }) {
  const summary = useMemo(() => tripSummary(run), [run]);
  const activity = useMemo(() => tripActivity(run), [run]);
  const points = useMemo(() => (run.stops || []).map((stop) => ({
    latitude: stop.latitude, longitude: stop.longitude, label: `Stop ${stop.sequence}`,
  })), [run.stops]);
  const planned = [distance(run.route?.distanceM), duration(run.route?.durationS)].filter(Boolean).join(' · ');
  const riderName = run.riderName || 'Rider';
  const links = [
    can('delivery.dispatch.read.business') && [`/business/delivery?view=parcels&memberId=${run.riderBusinessMemberId}`, `${riderName}'s parcels`],
    summary.failed > 0 && can('delivery.exceptions.manage.business') && ['/business/delivery?view=exceptions', 'Failed-stop decisions'],
    summary.cashParcels > 0 && can('delivery.cod.remit.business') && ['/business/delivery/settlement', 'Settlement desk'],
    summary.cashParcels > 0 && can('delivery.cod.read.business') && ['/business/finance/cod', 'COD custody in Finance'],
  ].filter(Boolean);

  return (
    <article aria-labelledby="trip-record-title" className="space-y-4">
      <section className={`p-4 sm:p-5 ${panel}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 id="trip-record-title" className="text-xl font-semibold">{run.businessName || 'Delivery'} - Trip #{run.id}</h2>
            <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-300">
              {can('delivery.riders.read.business')
                ? <Link to={`/business/delivery?view=riders&rider=${run.riderBusinessMemberId}`} className={textLink}>{riderName}</Link>
                : riderName}
              {summary.total ? ` · ${summary.total} ${summary.total === 1 ? 'stop' : 'stops'}` : ''}
            </p>
          </div>
          <span className="inline-flex shrink-0 rounded-full bg-neutral-100 px-3 py-1 text-sm font-semibold dark:bg-white/10">{tripStatusLabel(run)}</span>
        </div>

        <dl className="mt-4 grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
          <Fact term="Stops">{summary.total
            ? [`${summary.delivered} delivered`, summary.failed ? `${summary.failed} failed` : null, summary.open ? `${summary.open} still open` : null, summary.removed ? `${summary.removed} removed` : null].filter(Boolean).join(' · ')
            : 'No stops on this trip'}</Fact>
          <Fact term="Planned route">{planned}</Fact>
          <Fact term="Cash on delivery">{summary.cashParcels
            ? `${peso(summary.cashCollectedCents)} collected of ${peso(summary.cashExpectedCents)} · ${summary.cashParcels} ${summary.cashParcels === 1 ? 'parcel' : 'parcels'}`
            : 'No cash to collect'}</Fact>
          {['PLANNED', 'READY'].includes(run.status)
            ? <Fact term="Parcels loaded">{run.load ? `${run.load.pickedUp ?? 0} of ${run.load.planned ?? summary.total} picked up` : null}</Fact>
            : <Fact term="Proofs">{summary.flaggedProofs ? `${summary.flaggedProofs} flagged for review` : summary.delivered ? 'None flagged' : 'None yet'}</Fact>}
          <Fact term="Planned">{dateTime(run.plannedAt)}</Fact>
          <Fact term="Pickup started">{dateTime(run.pickupStartedAt)}</Fact>
          <Fact term="Started delivering">{dateTime(run.startedAt)}</Fact>
          <Fact term={run.cancelledAt ? 'Cancelled' : 'Completed'}>{dateTime(run.cancelledAt || run.completedAt) || (run.status === 'COMPLETED' || run.status === 'CANCELLED' ? null : 'Not yet')}</Fact>
        </dl>

        {run.stoppedByEmergencyCash ? (
          <p className="mt-4 flex gap-2 rounded-xl border border-warning bg-warning-subtle p-3 text-sm text-warning">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />This trip was stopped early so the rider could hand in cash and parcels.
          </p>
        ) : null}
        {summary.cashParcels > 0 ? (
          <p className="mt-4 text-sm text-neutral-600 dark:text-neutral-300">Cash from this trip is counted when the rider hands it in at the Settlement desk. Finance then reconciles it under COD custody.</p>
        ) : null}
        {links.length ? (
          <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm">
            {links.map(([to, title]) => <li key={to}><Link to={to} className={`inline-flex min-h-9 items-center ${textLink}`}>{title}</Link></li>)}
          </ul>
        ) : null}
      </section>

      {can('delivery.pickups.release.business') ? <PickupHandoff key={`${businessId}:${run.id}`} businessId={businessId} run={run} onUpdated={onUpdated} /> : null}

      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]">
        <section aria-labelledby="trip-stops-title" className={`overflow-hidden ${panel}`}>
          <h3 id="trip-stops-title" className="border-b border-neutral-200 px-4 py-3 font-semibold dark:border-neutral-800">Stops and parcels</h3>
          {(run.stops || []).length ? (
            <ol className="divide-y divide-neutral-200 dark:divide-neutral-800">
              {run.stops.map((stop) => {
                const proof = stop.attempt?.proof;
                const failed = stopFailed(stop) && stop.status !== 'DELIVERED';
                const times = [stop.arrivedAt && `Arrived ${dateTime(stop.arrivedAt)}`, stop.completedAt && `Closed ${dateTime(stop.completedAt)}`].filter(Boolean).join(' · ');
                return (
                  <li key={stop.id} className="grid gap-x-3 gap-y-1 px-4 py-3 sm:grid-cols-[2rem_minmax(0,1fr)_auto]">
                    <span className="text-sm font-semibold tabular-nums text-neutral-600 dark:text-neutral-400" aria-label={`Stop ${stop.sequence}`}>{stop.sequence}</span>
                    <div className="min-w-0">
                      <Link className={`inline-flex min-h-9 items-center ${textLink}`} to={`/business/delivery?view=parcels&parcel=${stop.shipmentId}`}>{stop.orderReference || 'Parcel details'}</Link>
                      <p className="text-sm text-neutral-600 dark:text-neutral-300">{stop.destinationArea || stop.address || 'Destination on the parcel'}</p>
                      <p className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-neutral-600 dark:text-neutral-400">
                        {stop.attempt ? <span>Attempt {stop.attempt.number}{stop.attempt.status === 'FAILED' ? ' failed' : ''}</span> : <span>No attempt yet</span>}
                        {stop.cod ? <span>{peso(stop.cod.expectedAmountCents)} cash, {COD_STATUS[stop.cod.status] || readable(stop.cod.status).toLowerCase()}</span> : <span>Paid online</span>}
                        {proof ? (
                          can('delivery.proofs.review.business')
                            ? <Link to={`/business/delivery/proofs?show=all&proof=${proof.id}`} className={textLink}>{PROOF_STATUS[proof.status] || 'Proof'}</Link>
                            : <span>{PROOF_STATUS[proof.status] || 'Proof'}</span>
                        ) : null}
                        {proof?.reviewRequired ? <span className="inline-flex items-center gap-1 font-semibold text-warning"><Flag className="h-3 w-3" aria-hidden="true" />Proof flagged</span> : null}
                      </p>
                      {times ? <p className="mt-0.5 text-xs tabular-nums text-neutral-600 dark:text-neutral-400">{times}</p> : null}
                    </div>
                    <span className={`justify-self-start self-start rounded-full px-2.5 py-1 text-xs font-semibold sm:justify-self-end ${failed
                      ? 'bg-warning-subtle text-warning'
                      : stop.status === 'DELIVERED' ? 'bg-success-subtle text-success'
                        : 'bg-neutral-100 text-neutral-800 dark:bg-white/10 dark:text-neutral-100'}`}>{STOP_STATUS[stop.status] || readable(stop.status)}</span>
                  </li>
                );
              })}
            </ol>
          ) : <p className="px-4 py-4 text-sm text-neutral-600 dark:text-neutral-300">This trip has no stops.</p>}
        </section>

        <div className="space-y-4">
          <section aria-labelledby="trip-route-title" className={`p-4 ${panel}`}>
            <h3 id="trip-route-title" className="font-semibold">Route</h3>
            <OperationalMap points={points} encodedPolyline={run.route?.encodedPolyline || null} className="mt-3" />
            {!run.route?.encodedPolyline ? <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-300">The planned route line is unavailable; saved stop markers are shown.</p> : null}
          </section>

          <section aria-labelledby="trip-activity-title" className={`p-4 ${panel}`}>
            <h3 id="trip-activity-title" className="font-semibold">Activity</h3>
            {activity.length ? (
              <ol className="mt-3 space-y-2">
                {activity.map((event) => (
                  <li key={`${event.at}-${event.index}`} className="flex flex-wrap items-baseline justify-between gap-x-3 text-sm">
                    <span className="font-medium">{event.label}</span>
                    <time className="text-xs tabular-nums text-neutral-600 dark:text-neutral-400">{dateTime(event.at)}</time>
                  </li>
                ))}
              </ol>
            ) : <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-300">No timed events have been recorded for this trip yet.</p>}
            <p className="mt-3 text-xs text-neutral-600 dark:text-neutral-400">Only events with a recorded time are listed.</p>
          </section>
        </div>
      </div>
    </article>
  );
}
